import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ProduktionRow } from "@/components/ProduktionRow";
import { isPart, summary, openPdf } from "@/lib/produktion";
import { toast } from "sonner";
import { Factory, Printer, FileText } from "lucide-react";
import { SectionBody, SectionHead, SectionSummary } from "@/components/SectionCollapse";
import { RechnungFertigstellenOption } from "@/components/RechnungFertigstellenOption";
import { DokumentVorschauInhalt } from "@/components/DokumentVorschau";

const EDIT_ROLES = ["employee", "versand", "manager", "admin"];
const MANAGER = ["manager", "admin"];

function InvoiceOverride({ order, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fertig, setFertig] = useState(true);
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/auftraege/${order.id}/rechnung`, { fertigstellen: fertig });
      toast.success(r.data.entwurf ? `Rechnung ${r.data.rechnungsnummer} als Entwurf erstellt.` : `Rechnung ${r.data.rechnungsnummer} erstellt und fertiggestellt.`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Rechnung konnte nicht erstellt werden."); }
    finally { setBusy(false); setOpen(false); onChanged?.(); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { setFertig(true); setOpen(true); }} disabled={busy} data-testid="prod-invoice-override"><FileText className="w-4 h-4 mr-1" /> Rechnung jetzt erstellen</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Rechnung trotz offener Restmengen erstellen?</AlertDialogTitle>
            <AlertDialogDescription>Normalerweise entsteht die Rechnung erst mit dem letzten Paket. Die Rechnung enthält alle aktuellen Positionen (inkl. zusätzlicher Versandkosten). Mengen, die entfallen, bitte vorher über „Korrektur → Rest entfällt“ markieren bzw. die Positionen anpassen.</AlertDialogDescription>
          </AlertDialogHeader>
          {open && <DokumentVorschauInhalt orderId={order.id} typ="rechnung" />}
          <RechnungFertigstellenOption checked={fertig} onChange={setFertig} disabled={busy} testId="prod-invoice-fertigstellen" />
          <AlertDialogFooter><AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel><AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); if (!busy) create(); }} data-testid="prod-invoice-confirm">{busy ? "Rechnung wird erstellt…" : fertig ? "Jetzt anlegen und fertigstellen" : "Jetzt als Entwurf anlegen"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function ProduktionSection({ order, user, onChanged, collapseKey }) {
  const parts = (order.positionen || []).filter(isPart);
  const s = summary(order.positionen);
  const canEdit = EDIT_ROLES.includes(user?.rolle);
  const canDrop = MANAGER.includes(user?.rolle);
  const print = () => openPdf(`/auftraege/${order.id}/produktionsblatt.pdf`, `Produktionsblatt_${order.auftragsnummer}.pdf`).catch((e) => toast.error(`Produktionsblatt konnte nicht erzeugt werden. ${e.message}`));
  return (
    <Card className="border-slate-200" data-testid="produktion-section">
      <CardContent className="p-5 space-y-3">
        <SectionHead k={collapseKey} label="Produktion" className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-semibold flex items-center gap-2"><Factory className="w-4 h-4" /> Produktion</h2>
          {parts.length > 0 && <span className="text-sm text-slate-600" data-testid="prod-summary"><b>{s.fertig + s.versendet}/{s.menge}</b> fertig, <b>{s.versendet}</b> versendet{s.in_produktion > 0 && `, ${s.in_produktion} in Produktion`}{s.entfaellt > 0 && `, ${s.entfaellt} entfällt`}</span>}
          {parts.length === 0 && <SectionSummary k={collapseKey}>Keine Teile-Positionen</SectionSummary>}
          <div className="ml-auto flex gap-2">
            {canDrop && order.versand_status === "teilversendet" && !order.rechnungsnummer && !order.kostenlos && <InvoiceOverride order={order} onChanged={onChanged} />}
            <Button size="sm" variant="outline" onClick={print} data-testid="prod-produktionsblatt"><Printer className="w-4 h-4 mr-1" /> Produktionsblatt</Button>
          </div>
        </SectionHead>
        <SectionBody k={collapseKey}>
        {parts.length === 0 ? <p className="text-sm text-slate-500">Keine Teile-Positionen vorhanden.</p> : (
          <ul className="divide-y divide-slate-100">
            {parts.map((p, i) => <ProduktionRow key={p.id} p={p} idx={i} orderId={order.id} canEdit={canEdit} canDrop={canDrop} onChanged={onChanged} />)}
          </ul>
        )}
        </SectionBody>
      </CardContent>
    </Card>
  );
}
